import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import {
  JsonArrayItem,
  emptyStructuredJsonItem,
  mergeStructuredJsonModel,
  structuredJsonItemFromPayload
} from '../../shared/design-system/structured-json-form.utils';

export type { JsonArrayItem };

export const PROPOSAL_STATUS_OPTIONS = [
  { value: 'Draft', labelKey: 'insurance.proposals.status.draft' },
  { value: 'Submitted', labelKey: 'insurance.proposals.status.submitted' },
  { value: 'Accepted', labelKey: 'insurance.proposals.status.accepted' },
  { value: 'Declined', labelKey: 'insurance.proposals.status.declined' },
  { value: 'Expired', labelKey: 'insurance.proposals.status.expired' },
  { value: 'Bound', labelKey: 'insurance.proposals.status.bound' }
] as const;

export const PAYMENT_PLAN_OPTIONS = [
  { value: 'monthly', labelKey: 'insurance.proposalDetail.paymentPlans.monthly' },
  { value: 'quarterly', labelKey: 'insurance.proposalDetail.paymentPlans.quarterly' },
  { value: 'yearly', labelKey: 'insurance.proposalDetail.paymentPlans.yearly' },
  { value: 'Cash', labelKey: 'insurance.proposalDetail.paymentPlans.cash' }
] as const;

export const INSURANCE_PROPOSAL_PAYMENT_PLAN_OPTIONS = PAYMENT_PLAN_OPTIONS;

export const INSURANCE_PROPOSAL_PARTY_FIELDS: StructuredJsonFieldDef[] = [
  { key: 'partyId', type: 'party', labelKey: 'insurance.proposalDetail.schemas.party.partyId', required: true }
];

export const INSURED_PARTY_ITEM_FIELDS = INSURANCE_PROPOSAL_PARTY_FIELDS;

export const INSURANCE_PROPOSAL_BENEFICIARY_FIELDS: StructuredJsonFieldDef[] = [
  { key: 'partyId', type: 'party', labelKey: 'insurance.proposalDetail.schemas.beneficiary.partyId', required: true },
  { key: 'sharePercent', type: 'number', labelKey: 'insurance.proposalDetail.schemas.beneficiary.sharePercent' },
  { key: 'relationship', type: 'string', labelKey: 'insurance.proposalDetail.schemas.beneficiary.relationship' }
];

export const BENEFICIARY_ITEM_FIELDS = INSURANCE_PROPOSAL_BENEFICIARY_FIELDS;

export function proposalStatusLabelKey(status: string): string {
  return `insurance.proposals.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
}

export function paymentPlanLabelKey(plan: string | null | undefined): string {
  if (!plan?.trim()) {
    return 'insurance.proposalDetail.paymentPlans.unspecified';
  }
  const match = PAYMENT_PLAN_OPTIONS.find(item => item.value.toLowerCase() === plan.trim().toLowerCase());
  return match?.labelKey ?? 'insurance.proposalDetail.paymentPlans.unknown';
}

export function parseProposalPartyArray(json: string, fields: StructuredJsonFieldDef[]): JsonArrayItem[] {
  if (!json?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(json) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map(item => {
      if (typeof item === 'string') {
        return mergeStructuredJsonModel(fields, { partyId: item });
      }
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        return mergeStructuredJsonModel(fields, item as JsonArrayItem);
      }
      return emptyStructuredJsonItem(fields);
    });
  } catch {
    return [];
  }
}

export function serializeProposalPartyArray(fields: StructuredJsonFieldDef[], items: JsonArrayItem[]): string {
  const cleaned = items
    .map(item => mergeStructuredJsonModel(fields, item))
    .map(item => {
      const partyId = typeof item['partyId'] === 'string' ? item['partyId'].trim() : '';
      if (!partyId) {
        return null;
      }

      if (fields.some(field => field.key === 'sharePercent')) {
        const payload: JsonArrayItem = { partyId };
        if (item['sharePercent'] !== null && item['sharePercent'] !== undefined && item['sharePercent'] !== '') {
          payload['sharePercent'] = item['sharePercent'];
        }
        if (typeof item['relationship'] === 'string' && item['relationship'].trim()) {
          payload['relationship'] = item['relationship'].trim();
        }
        return payload;
      }

      return { partyId };
    })
    .filter((item): item is JsonArrayItem => item !== null);

  return JSON.stringify(cleaned);
}

export function proposalPartyItemFromPayload(payloadJson: string): JsonArrayItem {
  return structuredJsonItemFromPayload(INSURANCE_PROPOSAL_PARTY_FIELDS, payloadJson);
}

export function proposalBeneficiaryItemFromPayload(payloadJson: string): JsonArrayItem {
  return structuredJsonItemFromPayload(INSURANCE_PROPOSAL_BENEFICIARY_FIELDS, payloadJson);
}

export function parsePartyIds(raw?: string | null): string[] {
  return parseProposalPartyArray(raw ?? '[]', INSURANCE_PROPOSAL_PARTY_FIELDS)
    .map(item => (typeof item['partyId'] === 'string' ? item['partyId'].trim() : ''))
    .filter(id => id.length > 0);
}

export function partyItemsFromIds(ids: string[]): JsonArrayItem[] {
  return ids.map(partyId => ({ partyId }));
}
